import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { api, assetUrl } from '../lib/api';
import { isSafeLinkUrl } from '../lib/urls';
import { usePayApplies } from '../hooks/usePayApplies';
import { PAY_THROUGH_ROPIDA_ANSWER, PAY_THROUGH_ROPIDA_FIRST_SESSION } from '../lib/payments';
// No mock data imports — all loaded from API
import { Badge, Btn, SectionHeading, AsanohaBackground, FAQ } from '../components';
import { palette as T, alpha, type } from '../design';

// ropida/info.jsx — About, Blog index, Blog post, Contact, Apply to teach, FAQ, Payout (Terracotta Studio)


function AboutPage({ setPage }) {
  const isMobile = useIsMobile();
  const [instructors, setInstructors] = React.useState([]);

  React.useEffect(() => {
    api.get('/instructors')
      .then(data => {
        if (data?.instructors) setInstructors(data.instructors.filter(i => i.status === 'active'));
      })
      .catch(() => setInstructors([]));
  }, []);

  const values = [
    { title: 'Vetted instructors', desc: 'No open signups. Instructors join through an application our team reviews, so every instructor here is someone we chose.' },
    { title: 'Flexible formats', desc: '1-on-1 or small-group sessions, each scheduled and priced by the instructor. Book when you want, how you want. Life happens — we get it.' },
    { title: 'Direct payment', desc: 'Students pay the instructor directly by GCash or bank transfer. Ropida takes no fee, so the full price of every session goes to the instructor.' },
    { title: 'Real humans', desc: 'Real instructors, real students, real conversations. Not an algorithm. Not an app that gamifies your way to nothing. Actual human connection that makes language stick.' },
  ];

  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, ' + T.ivory + ', ' + T.warmGrey + ')', borderBottom: '1px solid ' + T.border, padding: isMobile ? '48px 20px' : '80px 32px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Badge variant="terracotta">Our story</Badge>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 36 : 52, color: T.espresso, margin: '16px 0 20px', lineHeight: 1.1 }}>
            Language learning<br />that actually <span style={{ color: T.terracotta }}>blooms.</span>
          </h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.h4, color: T.walnut, lineHeight: 1.7, maxWidth: 580, margin: '0 auto' }}>
            Ropida started from a simple frustration: language learning is often cold, transactional, and forgettable. We wanted something warmer.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '64px 32px' }}>
        {/* Story */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 48, marginBottom: 64, alignItems: 'center' }}>
          <div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.terracotta, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Our story</p>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 32, color: T.espresso, margin: '0 0 20px', lineHeight: 1.2 }}>A neighborhood tea shop, not a supermarket.</h2>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, lineHeight: 1.75, marginBottom: 16 }}>
              We looked at the big language platforms and felt something was missing. Thousands of instructors, zero curation. Star ratings with no context. Booking flows that felt like buying plane tickets.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, lineHeight: 1.75, marginBottom: 16 }}>
              Ropida is a different bet: <strong style={{ color: T.espresso }}>fewer teachers, all exceptional.</strong> Warmer design, more human interaction. A marketplace where every instructor is someone we'd personally vouch for.
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, color: T.walnut, lineHeight: 1.75 }}>
              We started with Japanese and Mandarin because they're deeply meaningful languages — rich cultures, global opportunities, and genuinely hard enough that you need a great human teacher, not just flashcards.
            </p>
          </div>
          <div style={{ background: T.ivory, borderRadius: 24, padding: 32, border: '1px solid ' + T.border, textAlign: 'center' }}>
            <p style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: type.h3, color: T.espresso, fontStyle: 'italic', lineHeight: 1.5 }}>
              "We want every student to feel like they have a bilingual friend who happens to be a great teacher."
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, margin: '16px 0 0' }}>— The Ropida team</p>
          </div>
        </div>

        {/* Values */}
        <div style={{ marginBottom: 64 }}>
          <SectionHeading eyebrow="What we believe" title="Our values" center />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 20, marginTop: 36 }}>
            {values.map(v => (
              <div key={v.title} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 28, textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: T.ivory, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.h3, fontWeight: 700, color: T.terracotta, margin: '0 auto 16px', fontFamily: "'Lora', serif" }}>—</div>
                <h4 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.lead, color: T.espresso, margin: '0 0 10px' }}>{v.title}</h4>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, lineHeight: 1.65, margin: 0 }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div>
          <SectionHeading eyebrow="The people" title="Meet the instructors" center />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20, marginTop: 36 }}>
            {instructors.map(ins => (
              <div key={ins.id} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 20, textAlign: 'center', opacity: ins.status === 'coming_soon' ? 0.6 : 1 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', margin: '0 auto 12px', background: T.ivory, border: '3px solid ' + T.border }}>
                  {ins.photo ? <img src={assetUrl(ins.photo)} alt={ins.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, opacity: 0.3 }}>—</div>}
                </div>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, margin: '0 0 4px' }}>{ins.displayName}</p>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: '0 0 6px' }}>{ins.country}</p>
                {ins.status === 'coming_soon' ? <Badge variant="gold">Coming soon</Badge> : <Badge variant={ins.language === 'ZH' ? 'zh' : 'jp'}>{ins.language === 'ZH' ? 'Chinese' : 'Japanese'}</Badge>}
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 64 }}>
          <Btn size="lg" onClick={() => setPage('marketplace')}>Find your sensei →</Btn>
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── Blog Index ────────────────────────────────────────────────────────────────

function BlogPage({ setPage }) {
  const isMobile = useIsMobile();
  const [activeCategory, setActiveCategory] = React.useState('all');
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const categories = ['all', 'Japanese culture', 'Chinese culture', 'Study tips', 'Student stories'];

  React.useEffect(() => {
    api.get('/blog-posts')
      .then(data => {
        if (data?.posts) setPosts(data.posts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === 'all' ? posts : posts.filter(p => p.category === activeCategory);
  const featured = posts.find(p => p.featured);
  const rest = filtered.filter(p => !p.featured || activeCategory !== 'all');

  if (loading) {
    return (
      <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 32px', textAlign: 'center' }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading blog posts…</p>
        </div>
      </AsanohaBackground>
    );
  }

  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <SectionHeading eyebrow="Language learning journal" title="The Ropida Blog" subtitle="Culture tips, study strategies, and stories from the classroom." center />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginTop: 24 }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                padding: '7px 16px', borderRadius: 9999, border: '1px solid',
                borderColor: activeCategory === cat ? T.terracotta : T.border,
                background: activeCategory === cat ? T.ivory : T.white,
                color: activeCategory === cat ? T.terracotta : T.walnut,
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s'
              }}>
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Featured post */}
        {activeCategory === 'all' && featured && (
          <div onClick={() => setPage('blog-post-' + featured.id)} style={{
            background: T.white, borderRadius: 24, border: '1px solid ' + T.border, overflow: 'hidden',
            display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0, cursor: 'pointer',
            marginBottom: 40, boxShadow: '0 4px 24px ' + alpha('espresso', 'shadow')
          }}>
            <div style={{ position: 'relative', minHeight: isMobile ? 200 : 280, overflow: 'hidden' }}>
              <img src={featured.coverImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'} alt={featured.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '36px 36px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <Badge variant="terracotta">Featured</Badge>
                <Badge variant="sage">{featured.category}</Badge>
              </div>
              <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 12px', lineHeight: 1.25 }}>{featured.title}</h2>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, lineHeight: 1.65, margin: '0 0 20px' }}>{featured.excerpt}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{featured.author || 'Ropida Team'}</p>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: 0 }}>{featured.publishedAt ? new Date(featured.publishedAt).toLocaleDateString() : ''} · {featured.readTime || '5 min'} read</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {rest.map(post => (
            <div key={post.id} onClick={() => setPage('blog-post-' + post.id)} style={{
              background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden',
              cursor: 'pointer', transition: 'all 0.2s'
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 32px ' + alpha('espresso', 'shadow'); }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                <img src={post.coverImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600'} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 10, left: 10, background: alpha('espresso', 'scrim'), backdropFilter: 'blur(4px)', borderRadius: 9999, padding: '4px 10px' }}>
                  <span style={{ color: T.white, fontSize: type.label, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: 0.3 }}>{post.category}</span>
                </div>
              </div>
              <div style={{ padding: '18px 20px' }}>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.caption, color: T.driftwood, margin: '0 0 6px' }}>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : ''} · {post.readTime || '5 min'} read</p>
                <h4 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 17, color: T.espresso, margin: '0 0 8px', lineHeight: 1.3 }}>{post.title}</h4>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.5 }}>{(post.excerpt || '').slice(0, 100)}…</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── Blog Post ─────────────────────────────────────────────────────────────────

function BlogPostPage({ postId, setPage }) {
  const [post, setPost] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/blog-posts/${postId}`)
      .then(data => {
        if (data?.post) setPost(data.post);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postId]);

  const displayPost = post;

  if (loading) {
    return (
      <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 32px', textAlign: 'center' }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood }}>Loading article…</p>
        </div>
      </AsanohaBackground>
    );
  }

  if (!displayPost) {
    return (
      <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 32px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: '0 0 12px' }}>Post not found</h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '0 0 24px' }}>The article you are looking for does not exist.</p>
          <Btn onClick={() => setPage('blog')}>Back to blog</Btn>
        </div>
      </AsanohaBackground>
    );
  }

  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 32px' }}>
        <button onClick={() => setPage('blog')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 32, padding: 0 }}>← Back to blog</button>

        {/* Cover */}
        <div style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', marginBottom: 36, height: 360 }}>
          <img src={displayPost.coverImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'} alt={displayPost.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', bottom: 20, left: 20, background: alpha('espresso', 'scrim'), backdropFilter: 'blur(4px)', borderRadius: 9999, padding: '5px 12px' }}>
            <span style={{ color: T.white, fontSize: type.label, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: 0.3 }}>{displayPost.category}</span>
          </div>
        </div>

        {/* Meta */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {(displayPost.tags || []).map(tag => <Badge key={tag} variant="subtle">{tag}</Badge>)}
        </div>

        <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 38, color: T.espresso, margin: '0 0 20px', lineHeight: 1.15 }}>{displayPost.title}</h1>

        {/* Author bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 36, paddingBottom: 28, borderBottom: '1px solid ' + T.border }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{displayPost.author || 'Ropida Team'}</p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: 0 }}>{displayPost.publishedAt ? new Date(displayPost.publishedAt).toLocaleDateString() : ''} · {displayPost.readTime || '5 min'} read</p>
          </div>
        </div>

        {/* Content */}
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.lead, color: T.walnut, lineHeight: 1.9 }}>
          {(displayPost.content || '').split('\n\n').map((para, i) => {
            if (para.startsWith('**') && para.endsWith('**')) {
              return <h3 key={i} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '32px 0 12px' }}>{para.slice(2, -2)}</h3>;
            }
            if (para.startsWith('**')) {
              const match = para.match(/\*\*(.+?)\*\*(.*)/s);
              if (match) return <p key={i} style={{ margin: '0 0 20px' }}><strong style={{ color: T.espresso, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{match[1]}</strong>{match[2]}</p>;
            }
            return <p key={i} style={{ margin: '0 0 20px' }}>{para}</p>;
          })}
        </div>

        {/* Newsletter CTA */}
        <div style={{ background: T.ivory, borderRadius: 20, border: '1px solid ' + T.border, padding: 28, margin: '48px 0', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.lead, color: T.terracotta, margin: '0 0 6px', fontStyle: 'italic' }}>Want more? Keep going!</p>
          <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '0 0 12px' }}>Get language tips in your inbox</h3>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '0 0 20px' }}>Weekly study tips, cultural notes, and new posts from our instructors.</p>
          <div style={{ display: 'flex', gap: 8, maxWidth: 400, margin: '0 auto' }}>
            <input placeholder="Your email" style={{ flex: 1, padding: '10px 16px', borderRadius: 12, border: '1px solid ' + T.border, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, outline: 'none' }} />
            <Btn>Subscribe</Btn>
          </div>
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────

// The contact form's fields. `website` is the hidden bot field, as on the application form.
const EMPTY_CONTACT = { name: '', email: '', subject: '', message: '', website: '' };

// Labels and wording match the server's (backend/src/routes/publicForms.js), which still checks
// everything. Checking here first keeps a fixable mistake from spending the hourly limit.
const CONTACT_REQUIRED = [
  ['name', 'your name'],
  ['email', 'your email address'],
  ['subject', 'the subject'],
  ['message', 'your message'],
];

// The first problem the browser can see, worded as the server words it, or null.
function contactProblem(form) {
  for (const [field, label] of CONTACT_REQUIRED) {
    if (!form[field].trim()) return { field, message: `Please fill in ${label}, then send your message again.` };
  }
  if (!EMAIL_PATTERN.test(form.email.trim())) {
    return { field: 'email', message: 'Your email address does not look right. Please check it, then send your message again.' };
  }
  return null;
}

function ContactPage({ setPage }) {
  const isMobile = useIsMobile();
  // JTP-29: a student Pay applies to reads the Pay answer to How do I pay?
  const paysThroughRopida = usePayApplies();
  const [form, setForm] = React.useState(EMPTY_CONTACT);
  const [sending, setSending] = React.useState(false);
  // { message } once the server saved the message; { message, field } when it refused it.
  const [received, setReceived] = React.useState(null);
  const [problem, setProblem] = React.useState(null);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const invalid = (field) => problem?.field === field;

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const found = contactProblem(form);
    if (found) {
      setProblem(found);
      return;
    }
    setSending(true);
    setProblem(null);
    try {
      const body = { ...form, email: form.email.trim() };
      if (!body.website) delete body.website;
      const res = await api.post('/contact-messages', body);
      setReceived({ message: res.message });
    } catch (err) {
      setProblem({
        message: err.data?.message || 'We could not send your message. Please check your connection, then try again.',
        field: err.data?.field || null,
      });
    } finally {
      setSending(false);
    }
  };

  // Payment screenshots go on the booking with "I've paid", never by email (ruling from #320 review).
  const faq = [
    { q: 'How do I book my first session?', a: paysThroughRopida ? PAY_THROUGH_ROPIDA_FIRST_SESSION : 'Browse instructors, open a profile, and pick one of their upcoming sessions. You\'ll see the instructor\'s payment details (GCash or bank transfer) right after booking. Pay, then attach your payment screenshot to the booking: open My Bookings and tap "I\'ve paid". Your instructor confirms your seat once the payment arrives.' },
    { q: 'What timezone are class times shown in?', a: 'Session times are always shown in your own timezone (with the timezone name displayed), and you can switch it from the picker next to any session list.' },
    { q: 'How do I pay?', a: paysThroughRopida ? PAY_THROUGH_ROPIDA_ANSWER : 'Directly to your instructor — each booking shows their GCash or bank details (and QR code). After paying, open My Bookings and tap "I\'ve paid" to attach a screenshot of your payment to the booking. Your instructor sees it there. Your booking stays pending until they confirm it.' },
    { q: 'Is Ropida available outside the Philippines?', a: 'Yes! We have students in the US, UK, Canada, Australia, Singapore, Germany, and more. Instructors teach in English internationally.' },
    { q: 'How do group sessions work?', a: 'Some sessions have more than one seat — you\'ll see the seats left on each. Book early; once seats run out the session shows as Full.' },
  ];
  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '40px 16px' : '60px 32px' }}>
        <SectionHeading eyebrow="We're here" title="Talk to us" subtitle="Questions, partnership inquiries, or just want to say hello — we'd love to hear from you." center />

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 48, marginTop: 48, alignItems: 'start' }}>
          {/* Form */}
          <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: isMobile ? 24 : 32 }}>
            {received ? (
              <div role="status" style={{ textAlign: 'center', padding: '24px 0' }}>
                <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: '0 0 10px' }}>Message sent</h3>
                <p style={{ fontFamily: APPLY_FONT, fontSize: type.body, color: T.walnut, lineHeight: 1.6, margin: '0 0 20px' }}>{received.message}</p>
                <Btn variant="ghost" onClick={() => setPage('home')}>Back to home →</Btn>
              </div>
            ) : (
              <form onSubmit={submit} noValidate aria-label="Send a message">
                <h3 style={{ fontFamily: APPLY_FONT, fontWeight: 700, fontSize: type.h4, color: T.espresso, margin: '0 0 24px' }}>Send a message</h3>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="contact-name" style={APPLY_LABEL_STYLE}>Name</label>
                  <input id="contact-name" type="text" placeholder="Your name" autoComplete="name" required maxLength={100}
                    value={form.name} onChange={update('name')} aria-invalid={invalid('name')} style={applyInputStyle(invalid('name'))} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="contact-email" style={APPLY_LABEL_STYLE}>Email</label>
                  <input id="contact-email" type="email" placeholder="your@email.com" autoComplete="email" required maxLength={254}
                    value={form.email} onChange={update('email')} aria-invalid={invalid('email')} style={applyInputStyle(invalid('email'))} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="contact-subject" style={APPLY_LABEL_STYLE}>Subject</label>
                  <input id="contact-subject" type="text" placeholder="What's on your mind?" required maxLength={200}
                    value={form.subject} onChange={update('subject')} aria-invalid={invalid('subject')} style={applyInputStyle(invalid('subject'))} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="contact-message" style={APPLY_LABEL_STYLE}>Message</label>
                  <textarea id="contact-message" placeholder="Tell us what you're wondering about…" rows={4} required maxLength={5000}
                    value={form.message} onChange={update('message')} aria-invalid={invalid('message')} style={{ ...applyInputStyle(invalid('message')), resize: 'vertical' }} />
                </div>
                {/* The bot field: off screen and out of the tab order, so people never fill it. */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                    value={form.website} onChange={update('website')} />
                </div>
                {problem && (
                  <p role="alert" style={{ fontFamily: APPLY_FONT, fontSize: type.body, color: T.errorRed, lineHeight: 1.6, margin: '0 0 16px' }}>{problem.message}</p>
                )}
                <Btn disabled={sending} style={{ width: '100%', justifyContent: 'center' }}>{sending ? 'Sending…' : 'Send message →'}</Btn>
                <p style={{ fontFamily: APPLY_FONT, fontSize: type.label, color: T.driftwood, textAlign: 'center', margin: '12px 0 0' }}>We reply within two working days.</p>
                <p style={{ fontFamily: APPLY_FONT, fontSize: type.label, color: T.driftwood, textAlign: 'center', margin: '6px 0 0' }}>We use these details only to answer your message and delete them after 12 months.</p>
              </form>
            )}
          </div>

          {/* FAQ */}
          <div>
            <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.h4, color: T.espresso, margin: '0 0 20px' }}>Quick answers</h3>
            <FAQ items={faq} />
          </div>
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── Apply to Teach ────────────────────────────────────────────────────────────

// The application form's fields, in the order the visitor fills them. `website` is the hidden bot
// field: people never see it, so the server treats any value in it as a bot.
const EMPTY_APPLICATION = {
  name: '', email: '', language: '', yearsTeaching: '', certifications: '', introVideoUrl: '', about: '', website: '',
};

// The browser checks these before sending, so a fixable mistake never spends the visitor's hourly
// limit. Labels and wording match the server's (backend/src/routes/publicForms.js), which still
// checks everything.
const APPLICATION_REQUIRED = [
  ['name', 'your full name'],
  ['email', 'your email address'],
  ['language', 'the language you teach'],
  ['yearsTeaching', 'your years of teaching experience'],
  ['about', 'the text about yourself'],
];
// The email shape the server's schema accepts (ajv-formats "email", fast mode).
const EMAIL_PATTERN = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i;

// The first problem the browser can see, worded as the server words it, or null.
function applicationProblem(form) {
  for (const [field, label] of APPLICATION_REQUIRED) {
    if (!form[field].trim()) return { field, message: `Please fill in ${label}, then send your application again.` };
  }
  if (!EMAIL_PATTERN.test(form.email.trim())) {
    return { field: 'email', message: 'Your email address does not look right. Please check it, then send your application again.' };
  }
  const video = form.introVideoUrl.trim();
  if (video && !isSafeLinkUrl(video)) {
    return { field: 'introVideoUrl', message: 'Your intro video link must be a web address starting with https://. Please check it, then send your application again.' };
  }
  return null;
}

const APPLY_FONT ="'Plus Jakarta Sans', sans-serif";
const APPLY_LABEL_STYLE = { fontFamily: APPLY_FONT, fontWeight: 600, fontSize: type.body, color: T.espresso, display: 'block', marginBottom: 6 };

function applyInputStyle(invalid) {
  return {
    width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid ' + (invalid ? T.errorRed : T.border),
    fontFamily: APPLY_FONT, fontSize: type.body, outline: 'none', boxSizing: 'border-box', background: T.ivory,
  };
}

function ApplyToTeachPage({ setPage }) {
  const isMobile = useIsMobile();
  const [form, setForm] = React.useState(EMPTY_APPLICATION);
  const [sending, setSending] = React.useState(false);
  // { message } once the server saved the application; { message, field } when it refused it.
  const [received, setReceived] = React.useState(null);
  const [problem, setProblem] = React.useState(null);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const found = applicationProblem(form);
    if (found) {
      setProblem(found);
      return;
    }
    setSending(true);
    setProblem(null);
    try {
      const body = { ...form, email: form.email.trim() };
      // Leave out empty optional fields rather than send blanks.
      for (const key of ['certifications', 'introVideoUrl', 'website']) {
        if (!body[key]) delete body[key];
      }
      const res = await api.post('/applications', body);
      setReceived({ message: res.message });
    } catch (err) {
      setProblem({
        message: err.data?.message || 'We could not send your application. Please check your connection, then try again.',
        field: err.data?.field || null,
      });
    } finally {
      setSending(false);
    }
  };

  const invalid = (field) => problem?.field === field;
  const steps = [
    { num: 1, title: 'Submit your application', desc: 'Fill out the form below. Tell us about your teaching background, your language level, and why you want to teach on Ropida.' },
    { num: 2, title: 'We review (3–5 days)', desc: 'Our team reviews every application personally. We check credentials, experience, and cultural fit. Quality over quantity — always.' },
    { num: 3, title: 'Teaching demo', desc: 'Shortlisted applicants do a 20-minute demo lesson with a Ropida reviewer. We\'re looking for warmth, clarity, and genuine teaching skill.' },
    { num: 4, title: 'Welcome aboard', desc: 'Accepted instructors set up their profile, add the payment instructions students use to pay them, and publish their first session.' },
  ];
  const requirements = [
    'Native or near-native speaker of Japanese or Mandarin Chinese',
    'Teaching experience (formal or informal) — JLPT/HSK certifications a plus',
    'Stable internet, a decent webcam, and a quiet teaching environment',
    'Availability for at least 5 hours/week of teaching',
    'Conversational English to communicate with international students',
  ];
  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, ' + T.ivory + ', ' + T.warmGrey + ')', borderBottom: '1px solid ' + T.border, padding: isMobile ? '48px 20px' : '80px 32px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Badge variant="terracotta">For instructors</Badge>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 36 : 52, color: T.espresso, margin: '16px 0 16px', lineHeight: 1.1 }}>
            Share your language.<br /><span style={{ color: T.terracotta }}>Teach on Ropida.</span>
          </h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 15 : 17, color: T.walnut, lineHeight: 1.7, maxWidth: 500, margin: '0 auto 28px' }}>
            We're looking for exceptional Japanese and Mandarin instructors who love what they do. Students pay you directly by GCash or bank transfer, and Ropida takes no fee.
          </p>
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
            {[['Your price', 'You set the price of each session'], ['Paid directly', 'By GCash or bank transfer'], ['No Ropida fee', 'You keep the full price']].map(([n, d]) => (
              <div key={n} style={{ background: T.white, borderRadius: 14, padding: '12px 20px', border: '1px solid ' + T.border, textAlign: 'center' }}>
                <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 15, color: T.terracotta, margin: '0 0 2px' }}>{n}</p>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.walnut, margin: 0 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '40px 16px' : '64px 32px' }}>
        {/* Requirements */}
        <div style={{ marginBottom: 64 }}>
          <SectionHeading eyebrow="Who we're looking for" title="Our requirements" subtitle="We don't do open signups — every instructor is personally vetted. Here's what we look for." />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {requirements.map((r, i) => (
              <div key={i} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontFamily: "'Lora', serif", fontSize: type.lead, fontWeight: 700, color: T.terracotta, flexShrink: 0 }}>{i + 1}.</span>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.espresso, margin: 0, lineHeight: 1.6 }}>{r}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Process */}
        <div style={{ marginBottom: 64 }}>
          <SectionHeading eyebrow="How it works" title="The application process" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 20 }}>
            {steps.map(s => (
              <div key={s.num} style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: T.ivory, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: type.h4, fontWeight: 700, color: T.terracotta, fontFamily: "'Lora', serif" }}>{s.num}</div>
                  <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.caption, color: T.terracotta, textTransform: 'uppercase', letterSpacing: 1 }}>Step {s.num}</span>
                </div>
                <h4 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 17, color: T.espresso, margin: '0 0 8px' }}>{s.title}</h4>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Application form */}
        <div style={{ background: T.white, borderRadius: 24, border: '1px solid ' + T.border, padding: isMobile ? 24 : 40 }}>
          {received ? (
            <div role="status" style={{ textAlign: 'center', padding: '32px 0' }}>
              <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 26, color: T.espresso, margin: '0 0 12px' }}>Application received</h3>
              <p style={{ fontFamily: APPLY_FONT, fontSize: 15, color: T.walnut, lineHeight: 1.7, maxWidth: 420, margin: '0 auto 24px' }}>
                {received.message}
              </p>
              <Btn variant="ghost" onClick={() => setPage('home')}>Back to home →</Btn>
            </div>
          ) : (
            <form onSubmit={submit} noValidate aria-label="Apply to teach">
              <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '0 0 24px' }}>Apply to teach</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label htmlFor="apply-name" style={APPLY_LABEL_STYLE}>Full name</label>
                  <input id="apply-name" type="text" placeholder="Your full name" autoComplete="name" required maxLength={100}
                    value={form.name} onChange={update('name')} aria-invalid={invalid('name')} style={applyInputStyle(invalid('name'))} />
                </div>
                <div>
                  <label htmlFor="apply-email" style={APPLY_LABEL_STYLE}>Email</label>
                  <input id="apply-email" type="email" placeholder="your@email.com" autoComplete="email" required maxLength={254}
                    value={form.email} onChange={update('email')} aria-invalid={invalid('email')} style={applyInputStyle(invalid('email'))} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label htmlFor="apply-language" style={APPLY_LABEL_STYLE}>Language you teach</label>
                  <select id="apply-language" required value={form.language} onChange={update('language')}
                    aria-invalid={invalid('language')} style={applyInputStyle(invalid('language'))}>
                    <option value="">Select language</option>
                    <option>Japanese</option>
                    <option>Mandarin Chinese</option>
                    <option>Both</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="apply-years" style={APPLY_LABEL_STYLE}>Years of teaching experience</label>
                  <select id="apply-years" required value={form.yearsTeaching} onChange={update('yearsTeaching')}
                    aria-invalid={invalid('yearsTeaching')} style={applyInputStyle(invalid('yearsTeaching'))}>
                    <option value="">Select range</option>
                    <option>Less than 1 year</option>
                    <option>1–3 years</option>
                    <option>3–5 years</option>
                    <option>5+ years</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="apply-certifications" style={APPLY_LABEL_STYLE}>Certifications (optional)</label>
                <input id="apply-certifications" type="text" placeholder="e.g. JLPT N1, HSK 6, TESOL, university degree…" maxLength={300}
                  value={form.certifications} onChange={update('certifications')} aria-invalid={invalid('certifications')} style={applyInputStyle(invalid('certifications'))} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="apply-video" style={APPLY_LABEL_STYLE}>Intro video URL (optional)</label>
                <input id="apply-video" type="url" placeholder="Loom, YouTube, or Google Drive link" maxLength={500}
                  value={form.introVideoUrl} onChange={update('introVideoUrl')} aria-invalid={invalid('introVideoUrl')} style={applyInputStyle(invalid('introVideoUrl'))} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label htmlFor="apply-about" style={APPLY_LABEL_STYLE}>Tell us about yourself</label>
                <textarea id="apply-about" rows={4} required maxLength={3000}
                  placeholder="What do you love about teaching? What makes your sessions special? Any learning wins you're proud of…"
                  value={form.about} onChange={update('about')} aria-invalid={invalid('about')} style={{ ...applyInputStyle(invalid('about')), resize: 'vertical' }} />
              </div>
              {/* The bot field: off screen and out of the tab order, so people never fill it. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="apply-website">Website</label>
                <input id="apply-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                  value={form.website} onChange={update('website')} />
              </div>
              {problem && (
                <p role="alert" style={{ fontFamily: APPLY_FONT, fontSize: type.body, color: T.errorRed, lineHeight: 1.6, margin: '0 0 16px' }}>{problem.message}</p>
              )}
              <Btn disabled={sending} style={{ width: '100%', justifyContent: 'center' }}>{sending ? 'Sending…' : 'Submit application →'}</Btn>
              <p style={{ fontFamily: APPLY_FONT, fontSize: type.label, color: T.driftwood, textAlign: 'center', margin: '12px 0 0' }}>We review every application personally. Response within 3–5 working days.</p>
              <p style={{ fontFamily: APPLY_FONT, fontSize: type.label, color: T.driftwood, textAlign: 'center', margin: '6px 0 0' }}>We use these details only to answer your application and delete them after 12 months.</p>
            </form>
          )}
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── Instructor FAQ ─────────────────────────────────────────────────────────────

function InstructorFAQPage({ setPage }) {
  const isMobile = useIsMobile();
  const sections = [
    {
      title: 'Getting started',
      items: [
        { q: 'How do I apply to teach on Ropida?', a: 'Click "Apply to teach" in the footer or go to the For instructors section. Fill in the form — we review every application personally within 3–5 working days.' },
        { q: 'What languages can I teach?', a: 'Currently Ropida supports Japanese and Mandarin Chinese. We\'re planning to expand — if you teach another language, note it in your application.' },
        { q: 'Do I need a formal teaching certificate?', a: 'It helps but isn\'t required. We look for native or near-native fluency, proven teaching ability (formal or informal), and genuine enthusiasm. A demo lesson is part of the process.' },
        { q: 'How long does onboarding take?', a: 'About one week after acceptance. We\'ll help you set up your profile, introduce you to the platform, and match you with your first student.' },
      ]
    },
    {
      title: 'Teaching & scheduling',
      items: [
        { q: 'Can I set my own schedule?', a: 'Yes — you schedule sessions like meeting invites from your dashboard: title, date & time, duration, seats, and price. Students see and book them from your profile in their own timezone.' },
        { q: 'What platform do I teach on?', a: 'Any video platform you like — Zoom, Google Meet, or Teams. Paste your meeting link on the session and confirmed students see it in their dashboard.' },
        { q: 'How many students can I take on?', a: 'No cap. You set the seats per session — 1 for private lessons, up to 100 for group classes.' },
        { q: 'How do I get paid?', a: 'Students pay you directly (GCash or bank transfer) using the payment instructions and QR you set. You confirm each booking once the payment lands.' },
        { q: 'What happens if a student cancels last minute?', a: 'You\'ll get an email and the seat opens up again for someone else. Refunds for already-paid sessions are between you and the student.' },
      ]
    },
    {
      title: 'Rates & earnings',
      items: [
        { q: 'Can I set my own price?', a: 'Yes. You set the price of each session when you publish it.' },
        { q: 'When do I get paid?', a: 'Students pay you directly by GCash or bank transfer when they book a seat. Ropida never holds your money. You confirm the booking once the payment reaches you.' },
        { q: 'Does Ropida take a fee?', a: 'No. Ropida takes no fee, so you keep the full price of every session. There is no sign-up fee and no monthly fee.' },
      ]
    },
  ];
  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ background: 'linear-gradient(135deg, ' + T.ivory + ', ' + T.warmGrey + ')', borderBottom: '1px solid ' + T.border, padding: isMobile ? '48px 20px' : '72px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Badge variant="sage">For instructors</Badge>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 34 : 48, color: T.espresso, margin: '16px 0 16px', lineHeight: 1.1 }}>Instructor FAQ</h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? type.body : type.lead, color: T.walnut, lineHeight: 1.7, margin: '0 auto 24px', maxWidth: 480 }}>
            Everything you need to know about teaching on Ropida. Can't find your answer? <button onClick={() => setPage('contact')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.terracotta, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? type.body : type.lead, fontWeight: 600, padding: 0 }}>Contact us →</button>
          </p>
        </div>
      </div>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '40px 16px' : '64px 32px' }}>
        {sections.map(sec => (
          <div key={sec.title} style={{ marginBottom: 48 }}>
            <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? type.h3 : 24, color: T.espresso, margin: '0 0 20px', paddingBottom: 12, borderBottom: '2px solid ' + T.border }}>{sec.title}</h2>
            <FAQ items={sec.items} />
          </div>
        ))}
        <div style={{ background: T.ivory, borderRadius: 20, border: '1px solid ' + T.border, padding: 28, textAlign: 'center', marginTop: 16 }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.lead, color: T.terracotta, margin: '0 0 8px', fontStyle: 'italic' }}>Ready to teach?</p>
          <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 22, color: T.espresso, margin: '0 0 12px' }}>Join our instructor community</h3>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: '0 0 20px', lineHeight: 1.6 }}>Applications are open. We onboard 2–4 new instructors per month.</p>
          <Btn onClick={() => setPage('apply-to-teach')}>Apply to teach →</Btn>
        </div>
      </div>
    </AsanohaBackground>
  );
}

// ── Payout Info ────────────────────────────────────────────────────────────────

// Ropida takes no fee and makes no payouts: students pay the instructor directly (ADR 0003).
function PayoutInfoPage({ setPage }) {
  const isMobile = useIsMobile();
  const steps = [
    { num: 1, title: 'Add your payment instructions', desc: 'In your instructor dashboard, open Profile and fill in Payment details: your GCash number or bank account, and a QR code if you have one.' },
    { num: 2, title: 'A student books a seat', desc: 'The booking starts as pending. The student sees your payment instructions and the price of the session.' },
    { num: 3, title: 'The student pays you', desc: 'The student sends the payment to your GCash or bank account, then attaches a screenshot of it to the booking. You see it beside Confirm.' },
    { num: 4, title: 'You confirm the booking', desc: 'Once the payment reaches you, open Bookings and choose "Confirm — payment received". The student then sees the meeting link.' },
  ];
  const faq = [
    { q: 'Does Ropida take a fee?', a: 'No. Ropida takes no fee. The student pays you the full price of the session.' },
    { q: 'When do I receive the money?', a: 'When the student pays you, before the session. Students pay you directly, so Ropida never holds your money.' },
    { q: 'Can one session use different payment instructions?', a: 'Yes. Each session starts with the payment instructions from your profile. You can change them when you schedule the session.' },
    { q: 'What if the payment never arrives?', a: 'Leave the booking pending until it does. If the student does not pay, decline the booking to open the seat for someone else.' },
    { q: 'How do refunds work?', a: 'Refunds are between you and the student, because the student paid you directly.' },
  ];
  return (
    <AsanohaBackground style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ background: 'linear-gradient(135deg, ' + T.warmGrey + ', ' + T.ivory + ')', borderBottom: '1px solid ' + T.border, padding: isMobile ? '48px 20px' : '72px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Badge variant="sage">For instructors</Badge>
          <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 34 : 48, color: T.espresso, margin: '16px 0 16px', lineHeight: 1.1 }}>How you get paid</h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? type.body : type.lead, color: T.walnut, lineHeight: 1.7, margin: '0 auto', maxWidth: 440 }}>
            Students pay you directly by GCash or bank transfer. Ropida takes no fee.
          </p>
        </div>
      </div>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '40px 16px' : '64px 32px' }}>
        {/* Payment model */}
        <div style={{ marginBottom: 56 }}>
          <SectionHeading eyebrow="How it works" title="Your money comes straight to you" />
          <div style={{ background: T.white, borderRadius: 24, border: '1px solid ' + T.border, padding: isMobile ? 24 : 36, marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 20 : 32, textAlign: 'center', marginBottom: 28 }}>
              {[['Direct', 'Students pay you', 'By GCash or bank transfer'], ['No fee', 'Ropida takes nothing', 'You keep the full price'], ['You confirm', 'Seat confirmed', 'When the payment reaches you']].map(([val, label, sub]) => (
                <div key={label}>
                  <p style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 30 : 36, color: T.terracotta, margin: '0 0 4px' }}>{val}</p>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, margin: '0 0 4px' }}>{label}</p>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, color: T.driftwood, margin: 0 }}>{sub}</p>
                </div>
              ))}
            </div>
            <div style={{ background: T.ivory, borderRadius: 16, padding: '16px 20px', borderTop: '2px dashed ' + T.border }}>
              <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: T.espresso }}>Example:</strong> You price a session at ₱1,200. A student books a seat and sends you ₱1,200 by GCash. You confirm the booking and keep the full ₱1,200.
              </p>
            </div>
          </div>
        </div>

        {/* From booking to confirmed */}
        <div style={{ marginBottom: 56 }}>
          <SectionHeading eyebrow="Step by step" title="From booking to confirmed" />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 16 }}>
            {steps.map(s => (
              <div key={s.num} style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: 20, display: 'flex', gap: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: T.ivory, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: T.terracotta, fontFamily: "'Lora', serif", flexShrink: 0 }}>{s.num}</div>
                <div>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.body, color: T.espresso, margin: '0 0 4px' }}>{s.title}</p>
                  <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.55 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div style={{ marginBottom: 40 }}>
          <SectionHeading eyebrow="Questions" title="Payment FAQ" />
          <FAQ items={faq} />
        </div>

        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, marginBottom: 16 }}>Still have questions about getting paid?</p>
          <Btn onClick={() => setPage('contact')}>Contact us →</Btn>
        </div>
      </div>
    </AsanohaBackground>
  );
}

export { AboutPage, BlogPage, BlogPostPage, ContactPage, ApplyToTeachPage, InstructorFAQPage, PayoutInfoPage };
